import React from 'react';
import { Edit as EditIcon, Warning as AllergyIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import type { Allergy } from '../types';

export type { Allergy };

interface PatientAllergiesCardProps {
  allergies: Allergy[];
  isLoading?: boolean;
  onEdit?: () => void;
}

const isAllergyActive = (allergy: Allergy): boolean => allergy.is_active !== false;

export const PatientAllergiesCard: React.FC<PatientAllergiesCardProps> = ({
  allergies,
  isLoading = false,
  onEdit,
}) => {
  const activeAllergies = allergies.filter(isAllergyActive);
  const inactiveAllergies = allergies.filter(a => !isAllergyActive(a));

  return (
    <Card
      sx={{
        width: '100%',
        minWidth: 400,
        maxWidth: 600,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 3,
          borderColor: 'borderColors.brand',
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <AllergyIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              color: 'textColors.darkText',
              mb: 0.25,
            }}
          >
            Patient Allergies
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
            <Typography
              variant="caption"
              sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}
            >
              {allergies.length} total • {activeAllergies.length} active
            </Typography>
            {onEdit && (
              <Chip
                label="+ Add"
                size="small"
                onClick={onEdit}
                sx={{
                  height: 18,
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              />
            )}
          </Box>
        </Box>
        {onEdit && (
          <Tooltip title="Edit allergies">
            <IconButton
              size="small"
              onClick={onEdit}
              aria-label="Edit allergies"
              sx={{
                color: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                },
              }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', maxHeight: 400 }}>
        {isLoading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Loading allergies...
            </Typography>
          </Box>
        ) : allergies.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No allergies recorded for this patient.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {/* Active Allergies */}
            {activeAllergies.length > 0 && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 600,
                    color: 'textColors.lightText',
                    fontSize: '0.625rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Active Allergies
                </Typography>
                <Stack spacing={1}>
                  {activeAllergies.map(allergy => (
                    <Box
                      key={allergy.allergy_id}
                      onClick={onEdit}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'backgroundColors.tertiary',
                        border: '1px solid',
                        borderColor: 'borderColors.lightprimary',
                        cursor: onEdit ? 'pointer' : 'default',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': onEdit
                          ? {
                              borderColor: 'primary.main',
                              bgcolor: 'action.hover',
                            }
                          : {},
                      }}
                    >
                      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: 'textColors.darkText',
                              mb: 0.25,
                            }}
                          >
                            {allergy.allergen || 'Unknown'}
                          </Typography>
                          {allergy.reaction && (
                            <Typography
                              variant="caption"
                              sx={{ fontSize: '0.6875rem', color: 'textColors.lightText', display: 'block' }}
                            >
                              Reaction: {allergy.reaction}
                            </Typography>
                          )}
                          {allergy.severity && (
                            <Chip
                              label={allergy.severity}
                              size="small"
                              color="warning"
                              sx={{ height: 18, fontSize: '0.625rem', mt: 0.5 }}
                            />
                          )}
                        </Box>
                        <Chip
                          label="Active"
                          size="small"
                          color="success"
                          sx={{ height: 18, fontSize: '0.625rem' }}
                        />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Inactive Allergies */}
            {inactiveAllergies.length > 0 && (
              <Box>
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 600,
                    color: 'textColors.lightText',
                    fontSize: '0.625rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  Inactive Allergies
                </Typography>
                <Stack spacing={1}>
                  {inactiveAllergies.slice(0, 3).map(allergy => (
                    <Box
                      key={allergy.allergy_id}
                      onClick={onEdit}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'backgroundColors.tertiary',
                        border: '1px solid',
                        borderColor: 'borderColors.lightprimary',
                        opacity: 0.7,
                        cursor: onEdit ? 'pointer' : 'default',
                        transition: 'all 0.15s ease-in-out',
                        '&:hover': onEdit
                          ? {
                              borderColor: 'primary.main',
                              bgcolor: 'action.hover',
                              opacity: 1,
                            }
                          : {},
                      }}
                    >
                      <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: 'textColors.darkText',
                            }}
                          >
                            {allergy.allergen || 'Unknown'}
                          </Typography>
                        </Box>
                        <Chip
                          label="Inactive"
                          size="small"
                          sx={{ height: 18, fontSize: '0.625rem', bgcolor: 'backgroundColors.secondary' }}
                        />
                      </Stack>
                    </Box>
                  ))}
                  {inactiveAllergies.length > 3 && (
                    <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'textColors.lightText', pl: 1 }}>
                      ...and {inactiveAllergies.length - 3} more
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
